import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../environments/environment';
import { APPROVE_BATCH_MAX, ApproveBatchResult, ApproveMembersRequest, ApproveMembersResponse, OrgCapacityView, OwnedPartner, PartnerDocumentsRequest, PartnerInfoRequest, PartnerMember } from '../models/partners.model';

/** Partner üyeliği: yönetilen kurumlar, kurum bilgisi, sıralı onay belgeleri ve üyeler. Sunucu asıl bekçidir. */
@Injectable({ providedIn: 'root' })
export class PartnersService {
    private readonly http = inject(HttpClient);
    private readonly base = `${environment.kartexApiUrl}/v2/Partners`;
    private readonly orgBase = `${environment.kartexApiUrl}/v2/Org`;

    owned(): Observable<OwnedPartner[]> {
        // Ent web oturumunda da çağrılır: sunucu şirket hesabı adına yanıtlar (panel QR F2).
        return this.http.get<OwnedPartner[]>(`${this.base}/Owned`);
    }

    /** İlk girişte organizasyon hesabı için kurum açar (201/200, Owned satırıyla aynı biçim). */
    create(name: string): Observable<OwnedPartner> {
        return this.http.post<OwnedPartner>(this.base, { name: name.trim() });
    }

    updateInfo(partnerId: string, body: PartnerInfoRequest): Observable<void> {
        return this.http.put<void>(`${this.base}/${encodeURIComponent(partnerId)}`, {
            name: body.name.trim(),
            membershipType: body.membershipType?.trim() || null,
            // null: önek değişmez; "": önek temizlenir (sunucu sözleşmesi).
            memberNoPrefix: body.memberNoPrefix === null ? null : body.memberNoPrefix.trim()
        });
    }

    /** Kurumun onay belgelerini sırasıyla yazar (dizideki sıra = mobildeki onay sırası). */
    setDocuments(partnerId: string, documentIds: string[]): Observable<void> {
        const body: PartnerDocumentsRequest = { documentIds };
        return this.http.put<void>(`${this.base}/${encodeURIComponent(partnerId)}/Documents`, body);
    }

    members(partnerId: string): Observable<PartnerMember[]> {
        return this.http.get<PartnerMember[]>(`${this.base}/${encodeURIComponent(partnerId)}/Members`);
    }

    /** Üye sınırı durumu (org token). */
    capacity(): Observable<OrgCapacityView> {
        return this.http.get<OrgCapacityView>(`${this.orgBase}/Capacity`);
    }

    /** Üye sınırı artırma talebi (spec §6.3/§6.4). Açık talep varsa sunucu onu döner (idempotent). */
    requestCapacity(): Observable<OrgCapacityView> {
        return this.http.post<OrgCapacityView>(`${this.orgBase}/CapacityRequest`, {});
    }

    /** Toplu onay; çağıran APPROVE_BATCH_MAX (100) ile parçalar. */
    approveBatch(ids: string[]): Observable<ApproveBatchResult> {
        return this.http.post<ApproveBatchResult>(`${this.orgBase}/Memberships/ApproveBatch`, { ids });
    }

    /** Bekleyen başvuruları onaylar; üye no sunucuda o anda verilir. Bir istekte 1..100 kimlik (fazlasını çağıran böler). */
    approveMembers(partnerId: string, membershipIds: string[]): Observable<ApproveMembersResponse> {
        if (membershipIds.length === 0 || membershipIds.length > APPROVE_BATCH_MAX) {
            throw new RangeError(`approveMembers: 1..${APPROVE_BATCH_MAX} kimlik beklenir`);
        }
        const body: ApproveMembersRequest = { membershipIds };
        return this.http.post<ApproveMembersResponse>(`${this.base}/${encodeURIComponent(partnerId)}/Members/Approve`, body);
    }

    /** Etkin ya da bekleyen üyeliği sonlandırır (zaten sonlanmışsa da 200); yanıt güncel üye satırıdır (status "Ended"). */
    endMember(partnerId: string, membershipId: string): Observable<PartnerMember> {
        return this.http.post<PartnerMember>(`${this.base}/${encodeURIComponent(partnerId)}/Members/${encodeURIComponent(membershipId)}/End`, {});
    }
}
